/**
 * Subscription Service
 * Manages user plan types, account types, and feature access permissions
 */

import { supabase } from '../lib/supabaseClient';

export type PlanType = 'standard' | 'pro' | 'pro_trial' | 'team' | 'business';
export type AccountType = 'personal' | 'business';

export interface UserSubscription {
  subscription_type: PlanType;
  account_type: AccountType;
  id: string;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
}

export interface FeaturePermissions {
  hasFullAccess: boolean;
  canUseAIChat: boolean;
  canCustomLabelExports: boolean;
  canCreateRetrofitPages: boolean;
  canEditRetrofitPages: boolean;
  canUseScanRoom: boolean;
  aiChatLimit: number | null; // null = unlimited
  scanRoomLimit: number | null; // null = unlimited
}

class SubscriptionService {
  private static instance: SubscriptionService;
  private cachedSubscription: UserSubscription | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  static getInstance(): SubscriptionService {
    if (!SubscriptionService.instance) {
      SubscriptionService.instance = new SubscriptionService();
    }
    return SubscriptionService.instance;
  }

  /**
   * Fetch user subscription from profiles table
   */
  async getUserSubscription(forceRefresh = false): Promise<UserSubscription | null> {
    // Return cached if valid and not forcing refresh
    if (!forceRefresh && this.cachedSubscription && Date.now() - this.cacheTimestamp < this.CACHE_DURATION) {
      return this.cachedSubscription;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('subscription_type, account_type, id, subscription_start_date, subscription_end_date')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('[SubscriptionService] Error fetching subscription:', error);
        return null;
      }

      // data will be null if no profile exists (not an error)
      if (!data) {
        return null;
      }

      this.cachedSubscription = data;
      this.cacheTimestamp = Date.now();
      return data;
    } catch (error) {
      console.error('[SubscriptionService] Error:', error);
      return null;
    }
  }

  /**
   * Get feature permissions based on user's plan type
   */
  async getFeaturePermissions(): Promise<FeaturePermissions> {
    const subscription = await this.getUserSubscription();
    
    if (!subscription) {
      // Default to most restrictive if can't fetch
      return this.getStandardPermissions();
    }

    const { subscription_type } = subscription;

    // Pro trial - full access but limited AI chat
    if (subscription_type === 'pro_trial') {
      return {
        hasFullAccess: false,
        canUseAIChat: true,
        canCustomLabelExports: true,
        canCreateRetrofitPages: true,
        canEditRetrofitPages: true,
        canUseScanRoom: true,
        aiChatLimit: 20,
        scanRoomLimit: null // unlimited scans
      };
    }

    // Pro plan
    if (subscription_type === 'pro') {
      return {
        hasFullAccess: true,
        canUseAIChat: true,
        canCustomLabelExports: true,
        canCreateRetrofitPages: true,
        canEditRetrofitPages: true,
        canUseScanRoom: true,
        aiChatLimit: 200,
        scanRoomLimit: null // unlimited scans
      };
    }

    // Team and Business plans
    if (['team', 'business'].includes(subscription_type)) {
      return {
        hasFullAccess: true,
        canUseAIChat: true,
        canCustomLabelExports: true,
        canCreateRetrofitPages: true,
        canEditRetrofitPages: true,
        canUseScanRoom: true,
        aiChatLimit: 200,
        scanRoomLimit: null // unlimited scans
      };
    }

    // Standard plan restrictions
    return this.getStandardPermissions();
  }

  /**
   * Standard plan permissions (restricted)
   */
  private getStandardPermissions(): FeaturePermissions {
    return {
      hasFullAccess: false,
      canUseAIChat: true, // can use but limited
      canCustomLabelExports: false,
      canCreateRetrofitPages: false,
      canEditRetrofitPages: false,
      canUseScanRoom: true, // can use but limited
      aiChatLimit: 20,
      scanRoomLimit: 20
    };
  }

  /**
   * Check if user has a specific feature
   */
  async hasFeature(feature: keyof Omit<FeaturePermissions, 'hasFullAccess' | 'aiChatLimit' | 'scanRoomLimit'>): Promise<boolean> {
    const permissions = await this.getFeaturePermissions();
    return permissions[feature];
  }

  /**
   * Get user's plan type
   */
  async getPlanType(): Promise<PlanType | null> {
    const subscription = await this.getUserSubscription();
    return subscription?.subscription_type || null;
  }

  /**
   * Get user's subscription start date for billing cycle calculations
   */
  async getSubscriptionStartDate(): Promise<Date | null> {
    const subscription = await this.getUserSubscription();
    if (!subscription?.subscription_start_date) return null;
    return new Date(subscription.subscription_start_date);
  }

  /**
   * Check if user is on standard plan
   */
  async isStandardPlan(): Promise<boolean> {
    const planType = await this.getPlanType();
    return planType === 'standard';
  }

  /**
   * Check if user has full access (pro/team/business/pro_trial)
   */
  async hasFullAccess(): Promise<boolean> {
    const permissions = await this.getFeaturePermissions();
    return permissions.hasFullAccess;
  }

  /**
   * Clear cache (useful after subscription changes)
   */
  clearCache(): void {
    this.cachedSubscription = null;
    this.cacheTimestamp = 0;
  }
}

export default SubscriptionService;
